import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {LinkContainer} from 'react-router-bootstrap';
import {useContext, useContextSelector} from 'use-context-selector';
import {Alert, Modal, Card, Form, InputGroup, Button} from 'react-bootstrap';
import {Helmet} from "react-helmet";
import Layout from '../common/Layout';
import {useTranslation} from 'react-i18next';
import Select from 'react-select'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faWifi} from '@fortawesome/free-solid-svg-icons/faWifi';
import {faSyncAlt} from '@fortawesome/free-solid-svg-icons/faSyncAlt';
import FieldContainer from '../common/FieldContainer';

import useOnFieldChange from '../util/useOnFieldChange';
import {LocalContext} from '../context/LocalContext';
import {SessionContext} from '../context/SessionContext';
import selectStyles from '../util/selectStyles';
import getStyledSelectIndicatorsContainer from '../util/getStyledSelectIndicatorsContainer';
import LayoutContentContainer from '../common/LayoutContentContainer';
import ModalRef from '../util/ModalRef';
import checkFieldValidity from '../util/checkFieldValidity';

// import useGetAllAPIOptions from '../providers/useGetAllAPIOptions';
// import useGetExpertiseOptionsCategories from '../providers/useGetAPIOptionsExpertsieCategories';

import useGetFields from '../providers/useGetFields';

import useFieldsProviderApplicationInitial from './useFieldsProviderApplicationInitial';
import useCreateClientStatusMessage from '../util/useCreateClientStatusMessage';

import useSubmitProviderApplication from './useSubmitProviderApplication';

const ProviderApplication = () => {
    const {t} = useTranslation();
    const {executeRecaptcha} = useGoogleReCaptcha();
    const createClientStatusMessage = useCreateClientStatusMessage();

    const isMounted = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, []);

    const cookiesPolicyAccepted = useContextSelector(LocalContext, v => v[0].cookiesPolicy.accepted);
    const clientOnline = useContextSelector(LocalContext, v => v[0].clientOnline);

    const UILangGuest = useContextSelector(LocalContext, v => v[0].UILang);
    const UILangSession = useContextSelector(SessionContext, v => v[0].UILang);

    const [UILang, setUILang] = useState(null);

    const [sessionData, setSessionData] = useContext(SessionContext);
    const modalVisible = useContextSelector(SessionContext, v => v[0].modalVisible);

    useEffect(() => {
        const currentUILang = cookiesPolicyAccepted ? UILangGuest : UILangSession;
        if (UILang !== currentUILang) {
            setUILang(currentUILang);
        }
    }, [cookiesPolicyAccepted, UILangGuest, UILangSession, setUILang, UILang]);

    const fieldsProviderApplicationInitial = useFieldsProviderApplicationInitial();

    const getAPIDataFields = useGetFields();


    const [isLoadingAPIDataFields, setIsLoadingAPIDataFields] = useState(false);
    const [isLoadingSubmitProviderApplication, setIsLoadingSubmitProviderApplication] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(
        () => {
            const loadingData = (
                isLoadingAPIDataFields ||
                isLoadingSubmitProviderApplication
            );

            if (!loading && loadingData) {
                setLoading(true);
            } else if (loading && !loadingData) {
                setLoading(false);
            }
        },
        [
            isLoadingAPIDataFields,
            isLoadingSubmitProviderApplication,
            loading,
            setLoading
        ]
    );

    const [APIDataFields, setAPIDataFields] = useState({});
    const [langAPIDataFields, setLangAPIDataFields] = useState(null);
    const [isErrorGetAPIDataFields, setIsErrorGetAPIDataFields] = useState(false);
    const [isLoadedAPIDataFields, setIsLoadedAPIDataFields] = useState(false);
    const [isUpdatedAPIDataFields, setIsUpdatedAPIDataFields] = useState(false);

    const handleGetAPIDataFields = useCallback(
        async () => {
            setIsLoadingAPIDataFields(true);
            setLangAPIDataFields(UILang);

            let responseAPIDataFields;

            try {
                responseAPIDataFields = await getAPIDataFields({lang: UILang, returnActive: false});
            } catch (error) {
                if (isMounted.current) {
                    createClientStatusMessage(error.clientStatusMessage);
                    setIsErrorGetAPIDataFields(true);
                    setIsLoadingAPIDataFields(false);
                }
            }

            if (isMounted.current) {
                setAPIDataFields(responseAPIDataFields);

                setIsLoadedAPIDataFields(true);
                setIsUpdatedAPIDataFields(true);

                console.log(responseAPIDataFields);

                setIsLoadingAPIDataFields(false);
            }
        },
        [
            UILang,
            setIsErrorGetAPIDataFields,
            setAPIDataFields,
            setIsLoadedAPIDataFields,
            setIsLoadingAPIDataFields,
            setIsUpdatedAPIDataFields,
            getAPIDataFields,
            createClientStatusMessage
        ]
    );

    useEffect(
        () => {
            if (
                clientOnline &&
                UILang !== null &&
                !isErrorGetAPIDataFields &&
                !isLoadingAPIDataFields &&
                (!isLoadedAPIDataFields || langAPIDataFields !== UILang)
            ) {
                handleGetAPIDataFields();
            }
        },
        [
            clientOnline,
            UILang,
            langAPIDataFields,
            isErrorGetAPIDataFields,
            isLoadingAPIDataFields,
            isLoadedAPIDataFields,
            setIsLoadingAPIDataFields,
            handleGetAPIDataFields
        ]
    );


    const [isInitializedFieldsProviderApplication, setIsInitializedFieldsProviderApplication] = useState(false);

    const [fieldsProviderApplication, setFieldsProviderApplication] = useState({});

    useEffect(
        () => {
            if (
                !isErrorGetAPIDataFields &&
                !loading &&
                isLoadedAPIDataFields &&
                (
                    !isInitializedFieldsProviderApplication ||
                    isUpdatedAPIDataFields
                )
            ) {
                if (isUpdatedAPIDataFields) {
                    setIsUpdatedAPIDataFields(false);
                }

                const optionsFieldIndustry = APIDataFields.industry.map((option) => {
                    return {
                        label: option.name,
                        value: option.id
                    }
                });

                const fieldIndustry = {
                    ...fieldsProviderApplicationInitial.fields.industry,
                    ...(fieldsProviderApplication.fields ? fieldsProviderApplication.fields.industry : {}),
                    config: {
                        ...fieldsProviderApplicationInitial.fields.industry.config,
                        ...(fieldsProviderApplication.fields ? fieldsProviderApplication.fields.industry.config : {}),
                        options: optionsFieldIndustry
                    }
                }

                const optionsFieldCountry = APIDataFields.country.map((option) => {
                    return {
                        label: t(`country.${option.name}`),
                        value: option.id
                    }
                });

                const fieldCountry = {
                    ...fieldsProviderApplicationInitial.fields.country,
                    ...(fieldsProviderApplication.fields ? fieldsProviderApplication.fields.country : {}),
                    config: {
                        ...fieldsProviderApplicationInitial.fields.country.config,
                        ...(fieldsProviderApplication.fields ? fieldsProviderApplication.fields.country.config : {}),
                        options: optionsFieldCountry
                    }
                }

                const categoryFields = {};
                APIDataFields.category.forEach((category, index) => {
                    const existingField = fieldsProviderApplication.fields ? fieldsProviderApplication.fields[`category-${category.id}`] : null;
                    categoryFields[`category-${category.id}`] = {
                        ...(existingField ? existingField : {}),
                        index: existingField ? existingField.index : index,
                        value: existingField ? existingField.value : '',
                        APIField: 'entity',
                        label: category.name,
                        touched: existingField ? existingField.touched : false,
                        required: category.required,
                        valid: existingField ? existingField.valid : !category.required,
                        validate: true,
                        config: {
                            variant: 'dropdown',
                            multiSelect: category.multi_select_submit_forms,
                            options: [...category.entity_list.map(entity => {return {label: entity.name, value: entity.id}})],
                        },
                        rules: [
                            ...(category.required ? [
                                {
                                    type: 'notEmpty'
                                }
                            ] : [
                                    {
                                        type: 'validOnEmpty'
                                    }
                                ]),
                            ...(category.multi_select_submit_forms ? [
                                {
                                    type: 'iterableMaxLength',
                                    length: 50
                                }
                            ] : []),
                            ...(existingField ? existingField.rules : [])
                        ],
                        name: `category-${category.id}`,
                    }
                });

                const fields = {
                    ...fieldsProviderApplicationInitial,
                    ...fieldsProviderApplication,
                    fields: {
                        ...fieldsProviderApplicationInitial.fields,
                        ...(fieldsProviderApplication.fields ? fieldsProviderApplication.fields : {}),
                        ...categoryFields,
                        industry: fieldIndustry,
                        country: fieldCountry
                    }
                }

                setFieldsProviderApplication(fields);

                console.log(fields);
                if (!isInitializedFieldsProviderApplication) {
                    setIsInitializedFieldsProviderApplication(true);
                }
            }
        },
        [
            isErrorGetAPIDataFields,
            loading,
            isLoadedAPIDataFields,
            isInitializedFieldsProviderApplication,
            isUpdatedAPIDataFields,
            fieldsProviderApplicationInitial,
            setIsUpdatedAPIDataFields,
            APIDataFields,
            fieldsProviderApplication,
            t,
            setIsInitializedFieldsProviderApplication
        ]
    )

    const submitProviderApplication = useSubmitProviderApplication();

    const revealInvalidProviderApplicationFields = useCallback(() => {
        const nextFields = {};
        let allValid = true;

        Object.entries(fieldsProviderApplication.fields).forEach(([fieldName, field]) => {
            if (!field.validate) {
                nextFields[fieldName] = field;
                return;
            }

            const validity = checkFieldValidity(field.value, field.rules, t);
            const valid = validity.valid;
            const hasMeaningfulValue = Array.isArray(field.value)
                ? field.value.length > 0
                : String(field.value).length > 0;
            const touched = field.touched || hasMeaningfulValue || !valid;
            const errorsFragment = validity.errors.map((error, index) => (
                <p className="mb-0" key={`${field.name || fieldName}-${index}`}>{error}</p>
            ));

            nextFields[fieldName] = {
                ...field,
                valid,
                touched,
                errors: validity.errors,
                errorsFragment,
            };

            allValid = allValid && valid;
        });

        setFieldsProviderApplication((previous) => ({
            ...previous,
            valid: allValid,
            touched: true,
            fields: nextFields,
        }));

        return allValid;
    }, [fieldsProviderApplication.fields, t]);

    const [isErrorSubmitProviderApplication, setIsErrorSubmitProviderApplication] = useState(false);
    const [isSubmittedProviderApplication, setIsSubmittedProviderApplication] = useState(false);

    const handleSubmitProviderApplication = useCallback(
        async () => {
            setIsLoadingSubmitProviderApplication(true);

            if (!executeRecaptcha) {
                createClientStatusMessage({
                    variant: 'danger',
                    i18nKey: 'error.external-captcha-error',
                    source: 'reCAPTCHA is not ready yet.'
                }, true);
                if (isMounted.current) {
                    setIsLoadingSubmitProviderApplication(false);
                    setIsErrorSubmitProviderApplication(true);
                }
                return
            }

            let recaptchaToken
            try {
                recaptchaToken = await executeRecaptcha();
            } catch (error) {
                console.log(error);
                createClientStatusMessage({
                    variant: 'danger',
                    i18nKey: 'error.external-captcha-error',
                    source: String(error && error.message ? error.message : error)
                }, true);
                if (isMounted.current) {
                    setIsLoadingSubmitProviderApplication(false);
                    setIsErrorSubmitProviderApplication(true);
                }
                return
            }

            let response;
            try {
                response = await submitProviderApplication(fieldsProviderApplication.fields, recaptchaToken);
            } catch (error) {
                if (isMounted.current) {
                    setIsLoadingSubmitProviderApplication(false);
                    setIsErrorSubmitProviderApplication(true);
                    createClientStatusMessage(error.clientStatusMessage);
                    console.log(error);
                }
                return
            }

            if (isMounted.current) {
                setIsLoadingSubmitProviderApplication(false);
                setIsSubmittedProviderApplication(true);

            }
            console.log(response);
        },
        [
            executeRecaptcha,
            fieldsProviderApplication,
            setIsLoadingSubmitProviderApplication,
            submitProviderApplication,
            setIsErrorSubmitProviderApplication,
            createClientStatusMessage
        ]
    );


    const onFieldProviderApplicationChange = useOnFieldChange(fieldsProviderApplication, setFieldsProviderApplication);


    return <Layout navSecondaryShow={true} className="bg-light" contentBackgroundArt={true}>
        <Helmet><title>{t('ProviderApplication.page-head-title')}</title></Helmet>
        {isSubmittedProviderApplication &&

            <Modal
                container={ModalRef}
                show={true}
                onHide={
                    () => {
                    }
                }
                onShow={
                    () => {
                    }
                }
                centered={true}
            >
                <Modal.Body>
                    <Alert
                        className="mb-0"
                        variant="info"
                    >
                        <p className="mb-0"><strong>{t('ProviderApplication.message-application-saved')}</strong></p>
                    </Alert>
                </Modal.Body>
                <Modal.Footer
                    className="bg-light"
                >
                    <LinkContainer to="/" exact={true}>
                        <Button
                            onClick={() => {
                                if (modalVisible) {
                                    setSessionData(
                                        {
                                            ...sessionData,
                                            modalVisible: false
                                        }
                                    )
                                }
                            }}
                            variant="primary">{t('ProviderApplication.go-to-home')}</Button>
                    </LinkContainer>
                </Modal.Footer>
            </Modal>
        }

        {isErrorSubmitProviderApplication &&
            <Modal
                container={ModalRef}
                show={true}
                onHide={
                    () => {
                    }
                }
                onShow={
                    () => {
                    }
                }
                centered={true}
            >
                <Modal.Body>
                    <Alert
                        className="mb-0"
                        variant="warning"
                    >
                        <p>{t('ProviderApplication.message-application-submission.p1')}</p>
                    </Alert>
                </Modal.Body>
                <Modal.Footer
                    className="bg-light"
                >
                    <Button variant="primary" onClick={() => {
                        setIsErrorSubmitProviderApplication(false);
                        if (modalVisible) {
                            setSessionData(
                                {
                                    ...sessionData,
                                    modalVisible: false
                                }
                            )
                        }
                    }}>{t('ProviderApplication.close')}</Button>
                </Modal.Footer>
            </Modal>
        }
        {
            !clientOnline && !isInitializedFieldsProviderApplication ?
                <Layout
                    style={
                        {
                            alignItems: 'center',
                            justifyContent: 'center'
                        }
                    }
                >
                    <div
                        className="p-4 text-muted"
                    >
                        <span><FontAwesomeIcon icon={faWifi} /></span>
                        <span
                            className="ml-2"
                        >{t('ProviderApplication.message-offline')}</span>
                    </div>
                </Layout> :
                (
                    isInitializedFieldsProviderApplication ?
                        <Form>
                            <LayoutContentContainer>
                                <h1 style={{fontWeight: 400}}>{t('ProviderApplication.provider-application')}</h1>
                                <div
                                    style={
                                        {
                                            marginTop: '2rem',
                                            marginBottom: '3rem'
                                        }
                                    }
                                >
                                    <p>{t('ProviderApplication.provider-application-introduction.p1')} <a target="_blank" href="/contact-us">{t('ProviderApplication.provider-application-introduction.p2')}</a>{t('ProviderApplication.provider-application-introduction.p3')}</p>
                                    <p>{t('ProviderApplication.acceptance-terms-review-request.p1')}<a target="_blank" href="/provider-acceptance-terms">{t('ProviderApplication.acceptance-terms-review-request.p2')}</a>{t('ProviderApplication.acceptance-terms-review-request.p3')}</p>
                                    <br />
                                    <p style={{fontSize: '0.85rem'}}><span className="form-field-container__required-field-indicator">*</span>{t('ProviderApplication.explanation-required-fields')}</p>
                                    <p
                                        style={
                                            {
                                                fontSize: '0.85rem',
                                                marginTop: '1rem'
                                            }
                                        }
                                    >{t('message-conditions-recaptcha.p1')}<a rel="noopener noreferrer" target="_blank" href="https://policies.google.com/privacy">{t('message-conditions-recaptcha.p2')}</a>{t('message-conditions-recaptcha.p3')}<a rel="noopener noreferrer" target="_blank" href="https://policies.google.com/terms">{t('message-conditions-recaptcha.p4')}</a> {t('message-conditions-recaptcha.p5')}</p>
                                </div>
                                <div className="provider-application__fields-group">
                                    <h4 className="">{t('ProviderApplication.fields-category-general-information')}</h4>

                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-name-or-title"
                                                style={
                                                    {
                                                        display: 'flex',
                                                        flexDirection: 'column'
                                                    }
                                                }
                                            >
                                                <div>{t('ProviderApplication.name-or-title')}{fieldsProviderApplication.fields.title.required && <span className="form-field-container__required-field-indicator">*</span>}</div>
                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-name-or-title"
                                                    value={fieldsProviderApplication.fields.title.value}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'title');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.title.valid && fieldsProviderApplication.fields.title.touched}
                                                    isValid={fieldsProviderApplication.fields.title.valid && fieldsProviderApplication.fields.title.touched}
                                                    // autoFocus={true}
                                                    disabled={loading}
                                                />
                                                <Form.Text className="text-muted mt-2">{t('ProviderApplication.name-or-title-help')}</Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.title.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />

                                    <FieldContainer
                                        label={

                                            <Form.Label
                                                htmlFor="provider-application-country"
                                                style={
                                                    {
                                                        display: 'flex',
                                                        flexDirection: 'column'
                                                    }
                                                }
                                            >
                                                <div>{t('ProviderApplication.country')}{fieldsProviderApplication.fields.country.required && <span className="form-field-container__required-field-indicator">*</span>}</div>
                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <div
                                                    style={
                                                        {
                                                            width: '100%'
                                                        }
                                                    }
                                                >
                                                    <Select
                                                        id="provider-application-country"
                                                        noOptionsMessage={() => t('Select.noOptionsMessage')}
                                                        loadingMessage={() => t('Select.loadingMessage')}
                                                        placeholder={t('Select.placeholder')}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        isDisabled={loading}
                                                        options={
                                                            fieldsProviderApplication.fields.country.config.options}
                                                        styles={selectStyles(fieldsProviderApplication.fields.country)}
                                                        onChange={
                                                            (value) => {
                                                                onFieldProviderApplicationChange(value ? value.value : '', 'country')
                                                            }
                                                        }
                                                        components={
                                                            {
                                                                IndicatorsContainer:
                                                                    getStyledSelectIndicatorsContainer(
                                                                        fieldsProviderApplication.fields.country.valid,
                                                                        fieldsProviderApplication.fields.country.touched
                                                                    )
                                                            }
                                                        }
                                                        value={
                                                            (() => {
                                                                if (fieldsProviderApplication.fields.country.value === null) {
                                                                    return null
                                                                }

                                                                for (const option of fieldsProviderApplication.fields.country.config.options) {
                                                                    if (fieldsProviderApplication.fields.country.value === option.value) {
                                                                        return option
                                                                    }
                                                                }
                                                            })()
                                                        }
                                                    />
                                                </div>
                                                <div className={!fieldsProviderApplication.fields.country.valid && fieldsProviderApplication.fields.country.touched ? `is-invalid` : null} />
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.country.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />

                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                className="d-flex"
                                                htmlFor="provider-application-industry">
                                                <div>{t('ProviderApplication.industry')}{fieldsProviderApplication.fields.country.required && <span className="form-field-container__required-field-indicator">*</span>}</div>
                                            </Form.Label>
                                        }
                                        control={
                                            <div
                                                style={
                                                    {
                                                        width: '100%'
                                                    }
                                                }
                                            >
                                                <Select
                                                    id="provider-application-industry"
                                                    noOptionsMessage={() => t('Select.noOptionsMessage')}
                                                    loadingMessage={() => t('Select.loadingMessage')}
                                                    placeholder={t('Select.placeholder')}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    isDisabled={loading}
                                                    options={fieldsProviderApplication.fields.industry.config.options}
                                                    styles={selectStyles(fieldsProviderApplication.fields.industry)}
                                                    components={
                                                        {
                                                            IndicatorsContainer:
                                                                getStyledSelectIndicatorsContainer(
                                                                    fieldsProviderApplication.fields.industry.valid,
                                                                    fieldsProviderApplication.fields.industry.touched
                                                                )
                                                        }
                                                    }
                                                    onChange={
                                                        (value) => {
                                                            onFieldProviderApplicationChange(value ? value.value : '', 'industry')
                                                        }
                                                    }
                                                    value={
                                                        (() => {
                                                            if (fieldsProviderApplication.fields.industry.value === null) {
                                                                return null
                                                            }

                                                            for (const option of fieldsProviderApplication.fields.industry.config.options) {
                                                                if (fieldsProviderApplication.fields.industry.value === option.value) {
                                                                    return option
                                                                }
                                                            }
                                                        })()
                                                    }
                                                />
                                                <div className={!fieldsProviderApplication.fields.industry.valid && fieldsProviderApplication.fields.industry.touched ? `is-invalid` : null} />
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.industry.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </div>
                                        }
                                    />

                                    {
                                        Object.values(fieldsProviderApplication.fields).filter((value) => {
                                            if (value.name.startsWith('category-')) {
                                                return true
                                            } else {
                                                return false
                                            }
                                        }).sort((a, b) => {
                                                if (a.index < b.index) {
                                                    return -1;
                                                } else if (a.index > b.index) {
                                                    return 1;
                                                }
                                                return 0;
                                            })
                                            .map((field) => <FieldContainer
                                                key={`provider-application-${field.name}`}
                                                label={
                                                    <Form.Label
                                                        className="d-flex"
                                                        htmlFor={`provider-application-${field.name}`} >
                                                        <div>{field.label}{field.required && <span className="form-field-container__required-field-indicator">*</span>}</div>
                                                    </Form.Label>
                                                }
                                                control={
                                                    <div
                                                        className="provider-application__form-group__field-data-control"
                                                    >
                                                        <div
                                                            style={
                                                                {
                                                                    width: '100%'
                                                                }
                                                            }
                                                        >
                                                            <Select
                                                                id="provider-application-expertise"
                                                                isMulti={true}
                                                                noOptionsMessage={() => t('Select.noOptionsMessage')}
                                                                loadingMessage={() => t('Select.loadingMessage')}
                                                                placeholder={t('Select.placeholder')}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                isDisabled={loading}
                                                                options={field.config.options}
                                                                styles={selectStyles(field)}
                                                                components={
                                                                    {
                                                                        IndicatorsContainer:
                                                                            getStyledSelectIndicatorsContainer(
                                                                                field.valid,
                                                                                field.touched
                                                                            )
                                                                    }
                                                                }
                                                                onChange={
                                                                    (value) => {
                                                                        onFieldProviderApplicationChange(value ? value.map(item => item.value) : [], field.name)
                                                                    }
                                                                }
                                                                value={
                                                                    (() => {
                                                                        const enabledValues = [];
                                                                        for (const enabledValue of field.value) {
                                                                            for (const option of field.config.options) {
                                                                                if (enabledValue === option.value) {
                                                                                    enabledValues.push(option);
                                                                                }
                                                                            }
                                                                        }
                                                                        return enabledValues;
                                                                    })()
                                                                }
                                                            />
                                                        </div>

                                                        <div className={!field.valid && field.touched ? `is-invalid` : null} />
                                                        <Form.Control.Feedback
                                                            type="invalid"
                                                        >
                                                            <div>{field.errorsFragment}</div>
                                                        </Form.Control.Feedback>
                                                    </div>
                                                }
                                            />)
                                    }
                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-logo-image">
                                                <div>{t('ProviderApplication.logo-image')}{fieldsProviderApplication.fields.logoImage.required && <span className="form-field-container__required-field-indicator">*</span>}</div>
                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <InputGroup
                                                    className={fieldsProviderApplication.fields.logoImage.valid ? 'is-valid' : 'is-invalid'}
                                                >
                                                    {fieldsProviderApplication.fields.logoImage.value.length > 0 ?
                                                        <InputGroup.Prepend>
                                                            <Button
                                                                variant="outline-danger"
                                                                disabled={loading || fieldsProviderApplication.fields.logoImage.value.length === 0}
                                                                onClick={
                                                                    () => {
                                                                        onFieldProviderApplicationChange([], 'logoImage')
                                                                    }

                                                                }
                                                            >
                                                                {t('ProviderApplication.clear')}
                                                            </Button>
                                                        </InputGroup.Prepend> : null
                                                    }
                                                    <Form.File
                                                        custom
                                                    >
                                                        <Form.File.Input
                                                            id="provider-application-logo-image"
                                                            accept=".png,.jpg,.jpeg"
                                                            onChange={
                                                                (event) => {
                                                                    onFieldProviderApplicationChange(event.target.files, 'logoImage')
                                                                }
                                                            }
                                                            disabled={loading}
                                                            isInvalid={!fieldsProviderApplication.fields.logoImage.valid && fieldsProviderApplication.fields.logoImage.touched}
                                                            isValid={fieldsProviderApplication.fields.logoImage.valid && fieldsProviderApplication.fields.logoImage.touched}
                                                        />
                                                        <Form.File.Label
                                                            data-browse={t('ProviderApplication.browse')}
                                                        >
                                                            <div
                                                                style={
                                                                    {
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        width: 'calc(100% - 6rem)'
                                                                    }
                                                                }
                                                            ><span>{fieldsProviderApplication.fields.logoImage.value.length !== 0 ? fieldsProviderApplication.fields.logoImage.value[0].name : <span className="text-muted">{t('ProviderApplication.logo-image-upload-requirements')}</span>}</span></div>
                                                        </Form.File.Label>


                                                    </Form.File>
                                                </InputGroup>
                                                <Form.Text className="text-muted">{t('ProviderApplication.logo-image-requirements.p1')}{t('ProviderApplication.logo-image-requirements.p2')}{fieldsProviderApplication.fields.logoImage.rules.find(rule => rule.type === 'fileMaxSizeMB').value.toString()}{t('ProviderApplication.logo-image-requirements.p3')}</Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.logoImage.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />
                                </div>
                                <div className="provider-application__fields-group">
                                    <h4 className="">{t('ProviderApplication.fields-category-description')}</h4>
                                    <p className="text-muted">{t('ProviderApplication.advice-description')}</p>
                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-short-summary">
                                                <div>{t('ProviderApplication.short-summary')} <span className="text-muted">({fieldsProviderApplication.fields.descriptionShort.value.length} / {fieldsProviderApplication.fields.descriptionShort.rules.find(rule => rule.type === 'maxLength').length})</span>{fieldsProviderApplication.fields.descriptionShort.required && <span className="form-field-container__required-field-indicator">*</span>}</div>

                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-short-summary"
                                                    as="textarea"
                                                    value={fieldsProviderApplication.fields.descriptionShort.value}
                                                    rows="3"
                                                    // placeholder={t('ProviderApplication.short-summary')}
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'descriptionShort');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.descriptionShort.valid && fieldsProviderApplication.fields.descriptionShort.touched}
                                                    isValid={fieldsProviderApplication.fields.descriptionShort.valid && fieldsProviderApplication.fields.descriptionShort.touched}
                                                />
                                                <Form.Text className="text-muted">{t('ProviderApplication.short-summary-help')}</Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.descriptionShort.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />

                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-full-description">
                                                <div>{t('ProviderApplication.full-description')} <span className="text-muted">({fieldsProviderApplication.fields.descriptionFull.value.length} / {fieldsProviderApplication.fields.descriptionFull.rules.find(rule => rule.type === 'maxLength').length})</span>{fieldsProviderApplication.fields.descriptionFull.required && <span className="form-field-container__required-field-indicator">*</span>}</div>

                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-full-description"
                                                    as="textarea"
                                                    value={fieldsProviderApplication.fields.descriptionFull.value}
                                                    rows="8"
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'descriptionFull');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.descriptionFull.valid && fieldsProviderApplication.fields.descriptionFull.touched}
                                                    isValid={fieldsProviderApplication.fields.descriptionFull.valid && fieldsProviderApplication.fields.descriptionFull.touched}
                                                />
                                                <Form.Text className="text-muted">{t('ProviderApplication.full-description-help')}</Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.descriptionFull.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />
                                </div>
                                <div className="provider-application__fields-group">
                                    <h4 className="">{t('ProviderApplication.fields-category-contact-information')}</h4>
                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-contact-email-address">
                                                <div>{t('ProviderApplication.email-address-public')}{fieldsProviderApplication.fields.emailPublic.required && <span className="form-field-container__required-field-indicator">*</span>}</div>

                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    type="emailPublic"
                                                    id="provider-application-emailPublic-address"
                                                    value={fieldsProviderApplication.fields.emailPublic.value}
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'emailPublic');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.emailPublic.valid && fieldsProviderApplication.fields.emailPublic.touched}
                                                    isValid={fieldsProviderApplication.fields.emailPublic.valid && fieldsProviderApplication.fields.emailPublic.touched}
                                                />
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.emailPublic.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                                <div style={{position: 'relative', marginLeft: '1.5rem', marginTop: '0.5rem'}}>
                                                    {/* <Form.Check.Input */}
                                                    {/*     id="provider-application-receive-status-updates" */}
                                                    {/*     type="checkbox" */}
                                                    {/*     checked={fieldsProviderApplication.fields.receiveReports.value} */}
                                                    {/*     readOnly */}
                                                    {/*     disabled={loading} */}
                                                    {/*     onChange={ */}
                                                    {/*         () => { */}
                                                    {/*             onFieldProviderApplicationChange(!fieldsProviderApplication.fields.receiveReports.value, 'receiveReports'); */}
                                                    {/*         } */}
                                                    {/*     } */}
                                                    {/*     style={ */}
                                                    {/*         { */}
                                                    {/*             pointerEvents: 'none' */}
                                                    {/*         } */}
                                                    {/*     } */}
                                                    {/* /> */}
                                                    {/* <Form.Check.Label htmlFor="provider-application-receive-status-updates">{t('ProviderApplication.receive-reports')}</Form.Check.Label> */}
                                                    {/* <Form.Text className="text-muted"> */}
                                                    {/*     {t('ProviderApplication.explanation-receive-reports')} */}
                                                    {/* </Form.Text> */}
                                                </div>
                                            </>
                                        }
                                    />
                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-contact-phone">
                                                <div>{t('ProviderApplication.contact-phone')}{fieldsProviderApplication.fields.phone.required && <span className="form-field-container__required-field-indicator">*</span>}</div>

                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <InputGroup>
                                                    <Form.Control
                                                        id="provider-application-contact-phone"
                                                        value={fieldsProviderApplication.fields.phone.value}
                                                        disabled={loading}
                                                        onChange={
                                                            (event) => {
                                                                onFieldProviderApplicationChange(event.target.value, 'phone');
                                                            }
                                                        }
                                                        isInvalid={!fieldsProviderApplication.fields.phone.valid && fieldsProviderApplication.fields.phone.touched}
                                                        isValid={fieldsProviderApplication.fields.phone.valid && fieldsProviderApplication.fields.phone.touched}
                                                    />
                                                </InputGroup>
                                                <div className={!fieldsProviderApplication.fields.phone.valid && fieldsProviderApplication.fields.phone.touched ? `is-invalid` : null} />
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.phone.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />

                                    <FieldContainer
                                        label={
                                            <Form.Label
                                                htmlFor="provider-application-website-address">
                                                <div>{t('ProviderApplication.website-address')}{fieldsProviderApplication.fields.website.required && <span className="form-field-container__required-field-indicator">*</span>}</div>

                                            </Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-website-address"
                                                    value={fieldsProviderApplication.fields.website.value}
                                                    // placeholder={t('ProviderApplication.website-address')}
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'website');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.website.valid && fieldsProviderApplication.fields.website.touched}
                                                    isValid={fieldsProviderApplication.fields.website.valid && fieldsProviderApplication.fields.website.touched}
                                                />
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.website.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />
                                </div>

                                <div className="provider-application__fields-group">
                                    <h4 className="">{t('ProviderApplication.fields-category-social-media')}</h4>
                                    <FieldContainer
                                        label={
                                            <Form.Label htmlFor="provider-application-socialFacebook">{t('ProviderApplication.social-facebook')}</Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-socialFacebook"
                                                    value={fieldsProviderApplication.fields.socialFacebook.value}
                                                    rows="3"
                                                    // placeholder={t('ProviderApplication.short-summary')}
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'socialFacebook');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.socialFacebook.valid && fieldsProviderApplication.fields.socialFacebook.touched}
                                                    isValid={fieldsProviderApplication.fields.socialFacebook.valid && fieldsProviderApplication.fields.socialFacebook.touched}
                                                />
                                                <Form.Text className="text-muted">
                                                    <div><em>facebook.com/{fieldsProviderApplication.fields.socialFacebook.value ? fieldsProviderApplication.fields.socialFacebook.value : " ..."}</em></div>
                                                </Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.socialFacebook.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />

                                    <FieldContainer
                                        label={

                                            <Form.Label htmlFor="provider-application-socialInstagram">{t('ProviderApplication.social-instagram')}</Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-socialInstagram"
                                                    value={fieldsProviderApplication.fields.socialInstagram.value}
                                                    rows="3"
                                                    // placeholder={t('ProviderApplication.short-summary')}
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'socialInstagram');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.socialInstagram.valid && fieldsProviderApplication.fields.socialInstagram.touched}
                                                    isValid={fieldsProviderApplication.fields.socialInstagram.valid && fieldsProviderApplication.fields.socialInstagram.touched}
                                                />
                                                <Form.Text className="text-muted">
                                                    <div><em>instagram.com/{fieldsProviderApplication.fields.socialInstagram.value ? fieldsProviderApplication.fields.socialInstagram.value : " ..."}</em></div>
                                                </Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.socialInstagram.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />
                                    <FieldContainer
                                        label={
                                            <Form.Label htmlFor="provider-application-socialTwitter">{t('ProviderApplication.social-twitter')}</Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-socialTwitter"
                                                    value={fieldsProviderApplication.fields.socialTwitter.value}
                                                    rows="3"
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'socialTwitter');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.socialTwitter.valid && fieldsProviderApplication.fields.socialTwitter.touched}
                                                    isValid={fieldsProviderApplication.fields.socialTwitter.valid && fieldsProviderApplication.fields.socialTwitter.touched}
                                                />
                                                <Form.Text className="text-muted">
                                                    <div><em>twitter.com/{fieldsProviderApplication.fields.socialTwitter.value ? fieldsProviderApplication.fields.socialTwitter.value : " ..."}</em></div>
                                                </Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.socialTwitter.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>
                                        }
                                    />
                                    <FieldContainer
                                        label={
                                            <Form.Label htmlFor="provider-application-socialLinkedInProfile">{t('ProviderApplication.social-linkedin')}</Form.Label>
                                        }
                                        control={
                                            <>
                                                <Form.Control
                                                    id="provider-application-socialLinkedInProfile"
                                                    value={fieldsProviderApplication.fields.socialLinkedInProfile.value}
                                                    rows="3"
                                                    disabled={loading}
                                                    onChange={
                                                        (event) => {
                                                            onFieldProviderApplicationChange(event.target.value, 'socialLinkedInProfile');
                                                        }
                                                    }
                                                    isInvalid={!fieldsProviderApplication.fields.socialLinkedInProfile.valid && fieldsProviderApplication.fields.socialLinkedInProfile.touched}
                                                    isValid={fieldsProviderApplication.fields.socialLinkedInProfile.valid && fieldsProviderApplication.fields.socialLinkedInProfile.touched}
                                                />
                                                <Form.Text className="text-muted">
                                                    <div><em>www.linkedin.com/{fieldsProviderApplication.fields.socialLinkedInProfile.value ? fieldsProviderApplication.fields.socialLinkedInProfile.value : " ..."}</em></div>
                                                </Form.Text>
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    <div>{fieldsProviderApplication.fields.socialLinkedInProfile.errorsFragment}</div>
                                                </Form.Control.Feedback>
                                            </>

                                        }
                                    />
                                </div>
                                <div className="provider-application__fields-group">
                                    <h4 className="">{t('ProviderApplication.fields-category-terms-of-service')}</h4>
                                    <div
                                        style={
                                            {
                                                paddingTop: '2.5rem',
                                            }
                                        }
                                    >
                                        <Form.Label
                                            htmlFor="provider-application-tos-confirmation"
                                            style={
                                                {
                                                    fontWeight: 500,
                                                }
                                            }
                                        >
                                            <div>{t('ProviderApplication.tos-confirmation')}{fieldsProviderApplication.fields.confirmTOS.required && <span className="form-field-container__required-field-indicator">*</span>}</div>

                                        </Form.Label>
                                        <Form.Group
                                            style={
                                                {
                                                    marginTop: '1rem'
                                                }
                                            }
                                        >
                                            <Card
                                                onClick={(event) => {
                                                    if (!loading && !event.target.attributes.href) {
                                                        onFieldProviderApplicationChange(!fieldsProviderApplication.fields.confirmTOS.value, 'confirmTOS');
                                                    }
                                                }}
                                                border={!loading ? fieldsProviderApplication.fields.confirmTOS.touched ? (fieldsProviderApplication.fields.confirmTOS.valid ? "success" : "danger") : "normal" : ""}
                                                className={`${fieldsProviderApplication.fields.confirmTOS.touched ? (!fieldsProviderApplication.fields.confirmTOS.valid ? 'is-invalid' : 'is-valid') : ''} shadow-sm`}
                                                style={
                                                    {
                                                        borderRadius: '20px'
                                                    }
                                                }
                                            >
                                                <Card.Body
                                                    style={
                                                        {
                                                            cursor: `pointer`,
                                                            padding: '1.75rem'
                                                        }
                                                    }
                                                >
                                                    <Form.Check
                                                    >
                                                        <Form.Check.Input
                                                            id="provider-application-tos-confirmation"
                                                            type="checkbox"
                                                            checked={fieldsProviderApplication.fields.confirmTOS.value}
                                                            readOnly
                                                            disabled={loading}
                                                            style={
                                                                {
                                                                    pointerEvents: 'none'
                                                                }
                                                            }
                                                        />
                                                        <Form.Check.Label
                                                            style={
                                                                {
                                                                    cursor: `pointer`,
                                                                    lineHeight: 1.8
                                                                }
                                                            }
                                                            className={`ml-3${loading ? " text-muted" : ""}`}
                                                        >
                                                            {
                                                                t('ProviderApplication.new-application.tos-confirmation-agreement.p1')}
                                                            <a href="/privacy-policy" target="_blank">
                                                                {t('ProviderApplication.new-application.tos-confirmation-agreement.p2')}
                                                            </a>
                                                            {t('ProviderApplication.new-application.tos-confirmation-agreement.p3')}
                                                            <a href="/privacy-policy" target="_blank">
                                                                {t('ProviderApplication.new-application.tos-confirmation-agreement.p4')}
                                                            </a>
                                                            {t('ProviderApplication.new-application.tos-confirmation-agreement.p5')}
                                                        </Form.Check.Label>
                                                    </Form.Check>
                                                </Card.Body>
                                            </Card>
                                            <Form.Control.Feedback
                                                type="invalid"
                                            >
                                                <div>{fieldsProviderApplication.fields.confirmTOS.errorsFragment}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div style={{display: 'flex', marginTop: '2rem'}}>
                                    <Button
                                        className="ml-auto"
                                        variant="primary"
                                        type="submit"
                                        onClick={
                                            (event) => {
                                                event.preventDefault()
                                                const isValid = revealInvalidProviderApplicationFields();
                                                if (!isValid) {
                                                    return;
                                                }
                                                handleSubmitProviderApplication();
                                            }
                                        }
                                        disabled={loading || !clientOnline || isSubmittedProviderApplication}
                                    >{isLoadingSubmitProviderApplication && <span className="mr-2"><FontAwesomeIcon icon={faSyncAlt} spin={true} /></span>}{t('ProviderApplication.submit-application')}</Button>
                                </div>
                            </LayoutContentContainer>
                        </Form> : null
                )
        }
    </Layout >
}

export default ProviderApplication;
